import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const AgentStatusIndicator = ({ status, name, description }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'completed': return 'text-green-700 bg-green-50 border-green-200';
      case 'in-progress': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'error': return 'text-red-700 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="w-5 h-5" />;
      case 'error': return <XCircleIcon className="w-5 h-5" />;
      case 'in-progress': return <ClockIcon className="w-5 h-5 animate-spin" />;
      default: return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <div className={`agent-status ${status} ${getStatusColor()}`}>
      {getStatusIcon()}
      <div>
        <div className="font-medium">{name}</div>
        <div className="text-sm opacity-75">{description}</div>
      </div>
    </div>
  );
};

export default function AuditPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [error, setError] = useState(null);
  const [agentStatus, setAgentStatus] = useState({
    auditor: 'pending'
  });

  const handleAudit = async (e) => {
    e.preventDefault();
    if (!url) return;

    setLoading(true);
    setError(null);
    setAuditResults(null);
    setAgentStatus({ auditor: 'in-progress' });

    try {
      const response = await axios.post('/api/audit', { url });
      setAuditResults(response.data);
      setAgentStatus({ auditor: 'completed' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to audit website');
      setAgentStatus({ auditor: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Website Audit</h1>
        
        {/* URL Input Form */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <form onSubmit={handleAudit} className="space-y-4">
            <div>
              <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                Website URL
              </label>
              <div className="mt-1">
                <input
                  type="url"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none transition-colors"
              style={{
                focusRingColor: '#6CB955',
                focusBorderColor: '#6CB955'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#6CB955';
                e.target.style.outline = '2px solid #6CB955';
                e.target.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.outline = 'none';
              }}
                  placeholder="https://example.com"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ 
                backgroundColor: '#6CB955',
                focusRingColor: '#4A9B3A'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9B3A'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6CB955'}
            >
              {loading ? 'Auditing Website...' : 'Start Audit'}
            </button>
          </form>
        </div>

        {/* Agent Status */}
        {(loading || auditResults || error) && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Agent Status</h2>
            <div className="space-y-3">
              <AgentStatusIndicator
                status={agentStatus.auditor}
                name="Auditor Agent"
                description="Analysing website structure, content, and brand information"
              />
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Audit Failed</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Results */}
        {auditResults && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Audit Results</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Brand Information</h3>
                  <div className="mt-2">
                    <p className="text-lg font-semibold text-gray-900">{auditResults.brandName}</p>
                    <p className="text-sm text-gray-600">{auditResults.location}</p>
                    <p className="text-sm text-gray-600 capitalize">{auditResults.propertyType?.replace('_', ' ')}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Description</h3>
                  <p className="mt-2 text-sm text-gray-900">{auditResults.description}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Brand Tone</h3>
                  <p className="mt-2 text-sm text-gray-900">{auditResults.tone}</p>
                </div>
              </div>

              {/* Amenities and Segments */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Target Segments</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {auditResults.targetSegments?.map((segment, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: 'rgba(108, 185, 85, 0.1)',
                        color: '#2C5F5D'
                      }}
                      >
                        {segment}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Amenities</h3>
                  <div className="mt-2 grid grid-cols-1 gap-1">
                    {auditResults.amenities?.slice(0, 10).map((amenity, index) => (
                      <span key={index} className="text-sm text-gray-900">• {amenity}</span>
                    ))}
                    {auditResults.amenities?.length > 10 && (
                      <span className="text-sm text-gray-500">+ {auditResults.amenities.length - 10} more</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Assessment */}
            {auditResults.seoAssessment && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">SEO Assessment</h3>
                
                {/* Overall Score */}
                <div className="mb-6 text-center">
                  <div className="text-3xl font-bold" style={{ color: '#6CB955' }}>{auditResults.seoAssessment.overallScore}</div>
                  <div className="text-sm text-gray-500">Overall SEO Score</div>
                </div>

                {/* Detailed Categories */}
                {auditResults.seoAssessment.categories && auditResults.seoAssessment.detailedAnalysis && (
                  <div className="space-y-4">
                    {Object.entries(auditResults.seoAssessment.categories).map(([key, score]) => {
                      const analysis = auditResults.seoAssessment.detailedAnalysis[key];
                      const categoryNames = {
                        titleOptimization: 'Title Optimization',
                        contentStructure: 'Content Structure',
                        localSEO: 'Local SEO',
                        technicalSEO: 'Technical SEO',
                        userExperience: 'User Experience'
                      };
                      
                      const getScoreColor = (score) => {
                        if (score >= 80) return '#6CB955';
                        if (score >= 60) return '#4A8B87';
                        return '#DC2626';
                      };

                      return (
                        <div key={key} className="border rounded-lg p-4" style={{ borderColor: '#E5E7EB' }}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{categoryNames[key] || key}</h4>
                            <span className="font-bold text-lg" style={{ color: getScoreColor(score) }}>{score}/100</span>
                          </div>
                          
                          {analysis && analysis.findings && analysis.findings.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 mb-1">Key Findings:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {analysis.findings.slice(0, 3).map((finding, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="mr-2 text-green-500">•</span>
                                    <span>{finding}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {analysis && analysis.recommendations && analysis.recommendations.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Recommendations:</p>
                              <ul className="text-sm space-y-1" style={{ color: '#6CB955' }}>
                                {analysis.recommendations.slice(0, 2).map((rec, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="mr-2">→</span>
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Fallback for legacy format */}
                {auditResults.seoAssessment.categories && !auditResults.seoAssessment.detailedAnalysis && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold" style={{ color: '#6CB955' }}>{auditResults.seoAssessment.overallScore}</div>
                      <div className="text-sm text-gray-500">Overall Score</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{auditResults.seoAssessment.categories.technical}</div>
                      <div className="text-sm text-gray-500">Technical</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{auditResults.seoAssessment.categories.content}</div>
                      <div className="text-sm text-gray-500">Content</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Optimization (AIO) Analysis */}
            {auditResults.aioAnalysis && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">AI Optimization Analysis</h3>
                
                {/* Overall Score */}
                <div className="mb-6 text-center">
                  <div className="text-3xl font-bold" style={{ color: '#4A8B87' }}>{auditResults.aioAnalysis.overallScore}</div>
                  <div className="text-sm text-gray-500">Overall AIO Score</div>
                </div>

                {/* Detailed Categories */}
                {auditResults.aioAnalysis.categories && auditResults.aioAnalysis.detailedAnalysis && (
                  <div className="space-y-4">
                    {Object.entries(auditResults.aioAnalysis.categories).map(([key, score]) => {
                      const analysis = auditResults.aioAnalysis.detailedAnalysis[key];
                      const categoryNames = {
                        naturalLanguageQueries: 'Natural Language Queries',
                        structuredDataAI: 'Structured Data for AI',
                        contentCompletenessLLM: 'Content Completeness for LLMs',
                        conversationalAI: 'Conversational AI Readiness',
                        localAIOptimization: 'Local AI Optimization'
                      };
                      
                      const getScoreColor = (score) => {
                        if (score >= 80) return '#4A8B87';
                        if (score >= 60) return '#6CB955';
                        return '#DC2626';
                      };

                      return (
                        <div key={key} className="border rounded-lg p-4" style={{ borderColor: '#E5E7EB' }}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{categoryNames[key] || key}</h4>
                            <span className="font-bold text-lg" style={{ color: getScoreColor(score) }}>{score}/100</span>
                          </div>
                          
                          {analysis && analysis.findings && analysis.findings.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 mb-1">Key Findings:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {analysis.findings.slice(0, 3).map((finding, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="mr-2 text-blue-500">•</span>
                                    <span>{finding}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {analysis && analysis.recommendations && analysis.recommendations.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">AI Recommendations:</p>
                              <ul className="text-sm space-y-1" style={{ color: '#4A8B87' }}>
                                {analysis.recommendations.slice(0, 2).map((rec, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="mr-2">🤖</span>
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Global Recommendations */}
                {auditResults.aioAnalysis.recommendations && auditResults.aioAnalysis.recommendations.length > 0 && (
                  <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(74, 139, 135, 0.1)' }}>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <span className="mr-2">🎯</span>
                      Priority AI Optimization Recommendations
                    </h4>
                    <ul className="text-sm space-y-2" style={{ color: '#2C5F5D' }}>
                      {auditResults.aioAnalysis.recommendations.slice(0, 5).map((rec, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2 font-bold">{idx + 1}.</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Action Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  // Store audit data and navigate to generate page
                  sessionStorage.setItem('auditData', JSON.stringify(auditResults));
                  window.location.href = '/generate';
                }}
                className="w-full sm:w-auto text-white px-6 py-3 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                style={{
                  backgroundColor: '#6CB955',
                  focusRingColor: '#4A9B3A'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9B3A'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6CB955'}
              >
                Generate Content Based on Audit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}